<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { UiMessage } from '@/types'
import CardForm from '@/components/CardForm.vue'
import Galeria from '@/components/Galeria.vue'
import BrickPay from '@/components/BrickPay.vue'
import { hasMp, type PaidPayload } from '@/payments'

const props = defineProps<{
  msg: UiMessage
  /** Las tarifas que ya están en el viaje. Es lo que pinta el "✓ En tu viaje". */
  enViaje?: string[]
  /** La compra ya se cerró: el paso de pago deja de pedir tarjeta. */
  pagado?: boolean
  /** El WhatsApp que el asesor ya recogió arriba, si lo recogió. */
  telefonoConocido?: string
  /**
   * Sube en uno cada vez que un intento de cobro TERMINA, salga bien o mal.
   *
   * Es lo que saca a la tarjeta de "Confirmando con el operador…". Antes solo
   * se salía de ahí al llegar el recibo, así que un cobro que fallaba dejaba el
   * letrero puesto para siempre: el cliente leía "no cierres esta ventana" y no
   * sabía si había pagado o no.
   */
  cobroCerrado?: number
}>()
const emit = defineEmits<{
  (e: 'action', payload: { token: string; label: string; paid?: PaidPayload }): void
}>()

/** ¿Esta tarifa ya está elegida? */
function yaElegido(ref: string | undefined): boolean {
  return !!ref && !!props.enViaje?.includes(ref)
}

/**
 * Entre que se manda la tarjeta y llega el recibo pasan varios segundos: se
 * autoriza, se reserva con el operador y se cobra. Sin decir nada, ese silencio
 * se siente como que el botón no funcionó — y la gente vuelve a picarle.
 */
const cobrando = ref(false)
/** El intento terminó sin recibo. No sabemos por qué; el asistente sí lo dice. */
const fallo = ref(false)
watch(() => props.pagado, (listo) => { if (listo) { cobrando.value = false; fallo.value = false } })
watch(() => props.cobroCerrado, () => {
  if (!cobrando.value) return
  cobrando.value = false
  // Si el cobro hubiera entrado, `pagado` ya sería true y el recibo estaría
  // pintado. Que no lo esté significa que algo se atravesó.
  fallo.value = !props.pagado
})

function money(amount: number, currency: string): string {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(amount)
}

const MEAL: Record<string, string> = {
  AI: 'Todo incluido', BB: 'Desayuno', HB: 'Media pensión', FB: 'Pensión completa', RO: 'Solo alojamiento', ZZ: 'Solo alojamiento',
}
function mealLabel(code: string): string {
  return MEAL[code] ?? code
}

/** Fotos del hotel: la galería si viene, si no la principal. */
function fotos(info: { photos?: string[]; photo?: string }): string[] {
  if (info.photos?.length) return info.photos
  return info.photo ? [info.photo] : []
}

/** Cómo se nombra cada concepto del viaje en una etiqueta corta. */
const ETIQUETA: Record<string, string> = {
  hotel: 'Hospedaje', paquete: 'Paquete', tour: 'Tour',
}

/* ---------- El WhatsApp del titular ----------
 *
 * No es un dato "de contacto por si acaso": CVU lo usa como **identificador
 * único** de la venta. Sin él la reserva se cobra y se confirma, pero queda
 * retenida en su Wallet esperando a que alguien consiga el número — una venta
 * cerrada que nadie puede liquidar.
 *
 * Por eso se pide AQUÍ y no se deja a que la conversación lo saque: en el paso
 * de pago la persona ya decidió, y una pregunta más no tira la venta. Si el
 * asesor ya lo tenía, viene puesto y solo hay que confirmarlo.
 */
const telefono = ref(props.telefonoConocido ?? '')
watch(() => props.telefonoConocido, (v) => { if (v && !telefono.value) telefono.value = v })

/** Diez dígitos es el mínimo de un número marcable en México. */
const telefonoSirve = computed(() => telefono.value.replace(/\D/g, '').length >= 10)

/** La tarjeta se tokenizó en el navegador → continuamos con el pago. */
function onPaid(paid: PaidPayload): void {
  cobrando.value = true
  const cola = paid.lastFour ? ` ····${paid.lastFour}` : ''
  emit('action', {
    token: 'pay',
    label: `Pagar${cola}`,
    // Se manda tal como lo escribió: no se le pone lada ni se reformatea.
    paid: { ...paid, phone: telefono.value.trim() },
  })
}
</script>

<template>
  <!-- Tarjeta de hotel -->
  <div
    v-if="msg.kind === 'hotel_card' && msg.hotelCard" class="rm-card"
    :class="{ elegido: yaElegido(msg.hotelCard.rateKeyRef) }"
  >
    <!-- Galería: un paquete se vende con sus fotos, no con una línea de precio -->
    <Galeria
      v-if="msg.hotelCard.photos?.length"
      :fotos="msg.hotelCard.photos" :nombre="msg.hotelCard.hotelName"
    />
    <div class="rm-card-body">
      <!-- El nombre como se dice. El del proveedor viene en mayúsculas y con la
           duración dentro, y ocupaba tres renglones para decir dos cosas. -->
      <div class="rm-hotel">{{ msg.hotelCard.titulo || msg.hotelCard.hotelName }}</div>
      <div class="rm-room">
        {{ msg.hotelCard.subtitulo || msg.hotelCard.duracion || msg.hotelCard.roomName }}
      </div>
      <ul v-if="msg.hotelCard.incluye?.length" class="rm-inc">
        <li v-for="linea in msg.hotelCard.incluye" :key="linea">{{ linea }}</li>
      </ul>
      <div class="rm-tags">
        <span v-if="msg.hotelCard.mealPlan" class="rm-tag">{{ msg.hotelCard.mealPlan }}</span>
        <span class="rm-tag" :class="msg.hotelCard.refundable ? 'ok' : 'no'">
          {{ msg.hotelCard.refundable ? 'Reembolsable' : 'No reembolsable' }}
        </span>
      </div>
      <!-- Lo que no es el paquete publicado se dice AQUÍ, pegado al precio y al
           botón de agregar, que es donde se decide. Tres mensajes más arriba ya
           no se lee. -->
      <p v-if="msg.hotelCard.aviso" class="rm-aviso">{{ msg.hotelCard.aviso }}</p>
    </div>
    <div class="rm-card-foot">
      <div class="rm-price">
        {{ money(msg.hotelCard.price, msg.hotelCard.currency) }}
        <!-- El total SIEMPRE con lo que cubre, cuando el origen lo manda: un
             importe sin unidad se divide mentalmente entre los viajeros. -->
        <span class="rm-unit">total<template v-if="msg.hotelCard.cubre"> · {{ msg.hotelCard.cubre }}</template></span>
      </div>
      <!-- Elegido, el botón deja de invitar y pasa a confirmar: quitarlo se
           hace desde el viaje, que es donde vive la lista. Un botón que suma y
           resta en el mismo lugar se presta a un clic de más. -->
      <button
        v-if="yaElegido(msg.hotelCard.rateKeyRef)"
        class="rm-btn puesto" type="button" disabled
      >✓ En tu viaje</button>
      <button
        v-else class="rm-btn"
        @click="emit('action', {
          token: 'select:' + (msg.hotelCard.rateKeyRef ?? ''),
          label: 'Agregué al viaje: ' + (msg.hotelCard.titulo ?? msg.hotelCard.hotelName),
        })"
      >Agregar al viaje</button>
    </div>
  </div>

  <!-- Circuito con salidas fijas.
       Tarjeta propia y NO la de hotel, por algo que no es estético: aquella
       lleva "Agregar al viaje", y un circuito hoy no se cierra en el chat. Un
       botón que promete lo que no puede cumplir es peor que ningún botón.
       Y las salidas van EN la tarjeta porque la pregunta número uno de este
       producto es "¿cuándo sale?". -->
  <div v-else-if="msg.kind === 'circuito' && msg.circuito" class="rm-circ">
    <Galeria
      v-if="msg.circuito.photos?.length"
      :fotos="msg.circuito.photos" :nombre="msg.circuito.nombre"
    />
    <div class="rm-card-body">
      <div class="rm-hotel">{{ msg.circuito.nombre }}</div>
      <div class="rm-room">
        {{ msg.circuito.duracion }}
        <template v-if="msg.circuito.recorrido.length">
          · {{ msg.circuito.recorrido.join(' · ') }}
        </template>
      </div>

      <div class="rm-sal-t">Salidas</div>
      <ul class="rm-sal">
        <li v-for="s in msg.circuito.salidas" :key="s.salidaId">
          <div class="rm-sal-f">
            {{ s.fechas }}
            <!-- El cupo se dice con palabras. Nunca un número: delata cuánto
                 compramos e invita a regatear. -->
            <span v-if="s.aviso" class="rm-ult">{{ s.aviso }}</span>
          </div>
          <div v-if="s.porPersona !== undefined" class="rm-sal-p">
            {{ money(s.porPersona, msg.circuito.currency) }}
            <span class="rm-unit">por persona</span>
          </div>
          <div v-else class="rm-sal-p pendiente">{{ s.sinPrecio }}</div>
        </li>
      </ul>

      <ul v-if="msg.circuito.notas?.length" class="rm-inc">
        <li v-for="n in msg.circuito.notas" :key="n">{{ n }}</li>
      </ul>
    </div>
    <div class="rm-card-foot">
      <!-- El «desde» solo si viene con su condición. Un importe pelón aquí casi
           siempre es el de cuádruple, y a una pareja le diría de menos. -->
      <div v-if="msg.circuito.desde !== undefined" class="rm-price">
        <span class="rm-unit">desde</span>
        {{ money(msg.circuito.desde, msg.circuito.currency) }}
        <span class="rm-unit">p/p · {{ msg.circuito.desdeCondicion }}</span>
      </div>
      <div v-else class="rm-price sin"><span class="rm-unit">Consulta el precio</span></div>
      <button
        class="rm-btn"
        @click="emit('action', {
          token: 'like',
          label: `Me interesa el circuito ${msg.circuito.nombre}`,
        })"
      >{{ msg.circuito.reservable ? 'Lo quiero' : 'Me interesa' }}</button>
    </div>
  </div>

  <!-- El viaje que se está armando.
       Es estado, no historia: el backend borra el anterior y publica este al
       final cada vez que cambia, para que el total de arriba nunca mienta. -->
  <div v-else-if="msg.kind === 'cart' && msg.cart" class="rm-cart">
    <div class="rm-cart-head">
      <span>✦ Tu viaje</span>
      <span class="rm-cart-n">{{ msg.cart.items.length }}</span>
    </div>
    <div v-for="it in msg.cart.items" :key="it.ref" class="rm-cart-row">
      <span class="rm-cart-tipo" :class="it.tipo">{{ ETIQUETA[it.tipo] }}</span>
      <div class="rm-cart-txt">
        <b>{{ it.titulo || it.nombre }}</b>
        <!-- Qué trae dentro. La tarjeta original ya quedó lejos hacia arriba y
             "PAQUETE CHIAPAS CLÁSICO" en mayúsculas no le dice nada a nadie. -->
        <span v-if="it.incluye?.length">{{ it.incluye.join(' · ') }}</span>
        <span v-else-if="it.detalle">{{ it.detalle }}</span>
        <!-- La unidad del importe, al lado del importe. Aquí es donde más
             tienta dividir: hay un total abajo y unos viajeros arriba. -->
        <span v-if="it.cubre" class="rm-cart-cubre">Precio para {{ it.cubre }}</span>
      </div>
      <span class="rm-cart-precio">{{ money(it.precio, it.moneda) }}</span>
      <button
        class="rm-cart-x" type="button" :aria-label="`Quitar ${it.nombre} del viaje`"
        @click="emit('action', { token: 'quitar:' + it.ref, label: `Quité de mi viaje: ${it.nombre}` })"
      >✕</button>
    </div>
    <div class="rm-cart-total">
      <span>Total</span>
      <b>{{ money(msg.cart.total, msg.cart.currency) }}</b>
    </div>
    <button
      class="rm-btn wide"
      @click="emit('action', { token: 'checkout', label: 'Ir a pagar' })"
    >Reservar y pagar</button>
    <p class="rm-cart-nota">Puedes seguir agregando tours antes de pagar.</p>
  </div>

  <!-- Tarjeta de pago -->
  <div
    v-else-if="msg.kind === 'payment' && msg.payment" class="rm-pay"
    :class="{ hecho: pagado, cobrando }"
  >
    <div class="rm-pay-head">
      <span class="rm-pay-title">{{ pagado ? '✓ Pagado' : 'Pago seguro' }}</span>
      <span class="rm-pay-amount">{{ money(msg.payment.amount, msg.payment.currency) }}</span>
    </div>

    <!-- Ya se cobró: el formulario desaparece. Dejar una tarjeta pidiendo datos
         debajo de un viaje ya pagado invita a pagar dos veces. -->
    <template v-if="pagado">
      <p class="rm-pay-note">Tu pago quedó. El detalle está abajo, con tu localizador.</p>
    </template>

    <!-- Los segundos en que se autoriza, se reserva y se cobra. Sin esto el
         silencio se siente como que el botón no sirvió. -->
    <template v-else-if="cobrando">
      <p class="rm-pay-note">Confirmando con el operador… no cierres esta ventana.</p>
      <div class="rm-pay-barra"><span></span></div>
    </template>

    <!-- Terminó y no hubo recibo. No se inventa el motivo —eso lo explica el
         asistente en su mensaje— pero SÍ se dice lo único que la persona
         necesita saber ya: que no se quedó a medias sin enterarse. -->
    <template v-else-if="fallo">
      <p class="rm-pay-falla">No se completó el cobro.</p>
      <p class="rm-pay-note">
        Abajo te digo qué pasó. Si quedó algún cargo, se libera solo.
      </p>
      <button type="button" class="rm-pay-otra" @click="fallo = false">
        Intentar con otra tarjeta
      </button>
    </template>

    <template v-else>
      <p class="rm-pay-note">
        Si la reserva no se confirma, no te quedas con el cargo.
      </p>

      <label class="rm-wa">
        <span>¿A qué WhatsApp te mandamos tu confirmación?</span>
        <input
          v-model="telefono" type="tel" inputmode="tel" autocomplete="tel"
          placeholder="10 dígitos"
          :class="{ falta: telefono.length > 0 && !telefonoSirve }"
        />
      </label>

      <!-- El pago no aparece hasta que hay número. No es un capricho de forma:
           cobrar sin él deja la venta retenida del lado del operador, y
           perseguir un teléfono después de cobrar es la peor conversación
           posible. -->
      <p v-if="!telefonoSirve" class="rm-wa-nota">
        Lo necesitamos para emitir tu reserva y mandarte el voucher.
      </p>
      <BrickPay
        v-else-if="hasMp" :amount="msg.payment.amount" :currency="msg.payment.currency" @paid="onPaid"
      />
      <CardForm
        v-else :amount="msg.payment.amount" :currency="msg.payment.currency" @paid="onPaid"
      />
      <div class="rm-pay-secure">🔒 Procesado por MercadoPago · tus datos nunca tocan nuestro sistema</div>
    </template>
  </div>

  <!-- Ficha informativa de hotel (datos reales del proveedor) -->
  <div v-else-if="msg.kind === 'hotel_info' && msg.hotelInfo" class="rm-info">
    <!-- Galería: se desliza de lado; las fotos cargan solo al verse -->
    <Galeria
      v-if="fotos(msg.hotelInfo).length"
      :fotos="fotos(msg.hotelInfo)" :nombre="msg.hotelInfo.name"
    />
    <div class="rm-info-body">
      <div class="rm-info-name">{{ msg.hotelInfo.name }}</div>
      <div v-if="msg.hotelInfo.destination || msg.hotelInfo.rating" class="rm-info-meta">
        <span v-if="msg.hotelInfo.destination">📍 {{ msg.hotelInfo.destination }}</span>
        <span v-if="msg.hotelInfo.rating">★ {{ msg.hotelInfo.rating.score }} · opiniones de viajeros</span>
      </div>
      <div v-if="msg.hotelInfo.porQue" class="rm-info-why">{{ msg.hotelInfo.porQue }}</div>
      <div v-if="msg.hotelInfo.facilities?.length" class="rm-tags">
        <span v-for="f in msg.hotelInfo.facilities" :key="f" class="rm-tag">{{ f }}</span>
      </div>
      <!-- Si la ficha viene de algo cotizado, el botón ES el de la venta.
           Mandar a "toca Elegir en la tarjeta de arriba" era un callejón: a los
           tres mensajes esa tarjeta ya quedó fuera de la pantalla. -->
      <button
        v-if="msg.hotelInfo.rateKeyRef"
        class="rm-btn wide"
        @click="emit('action', {
          token: 'select:' + msg.hotelInfo.rateKeyRef,
          label: 'Agregué al viaje: ' + msg.hotelInfo.name,
        })"
      >{{ yaElegido(msg.hotelInfo.rateKeyRef) ? '✓ En tu viaje' : 'Agregar al viaje' }}</button>
      <button
        v-else
        class="rm-btn wide"
        @click="emit('action', { token: 'like', label: `Me interesa el ${msg.hotelInfo.name}` })"
      >Me interesa este</button>
    </div>
  </div>

  <!-- Resumen de la solicitud (ficha en el chat) -->
  <div v-else-if="msg.kind === 'request_summary' && msg.requestSummary" class="rm-req">
    <div class="rm-req-head">✦ Resumen de tu solicitud</div>
    <div v-if="msg.requestSummary.origin" class="rm-req-row"><span>Sale de</span><b>{{ msg.requestSummary.origin }}</b></div>
    <div class="rm-req-row"><span>Destino</span><b>{{ msg.requestSummary.destination }}</b></div>
    <div v-if="msg.requestSummary.checkIn" class="rm-req-row"><span>Fechas</span><b>{{ msg.requestSummary.checkIn }} → {{ msg.requestSummary.checkOut }}</b></div>
    <div class="rm-req-row"><span>Huéspedes</span><b>{{ msg.requestSummary.adults }} adulto(s)<template v-if="msg.requestSummary.children"> · {{ msg.requestSummary.children }} menor(es)</template></b></div>
    <div v-if="msg.requestSummary.mealPlan" class="rm-req-row"><span>Plan</span><b>{{ mealLabel(msg.requestSummary.mealPlan) }}</b></div>
    <div v-if="msg.requestSummary.amenidades" class="rm-req-row"><span>Busca</span><b>{{ msg.requestSummary.amenidades }}</b></div>
    <div v-if="msg.requestSummary.hotel" class="rm-req-row"><span>Hotel</span><b>{{ msg.requestSummary.hotel }}</b></div>
    <div v-if="msg.requestSummary.budget" class="rm-req-row"><span>Presupuesto</span><b>{{ money(msg.requestSummary.budget, msg.requestSummary.currency || 'MXN') }}</b></div>
  </div>

  <!-- Resumen de reserva -->
  <div v-else-if="msg.kind === 'booking_summary' && msg.bookingSummary" class="rm-book">
    <div class="rm-book-brillo" aria-hidden="true"></div>
    <div class="rm-book-check"><span class="rm-book-tache">✓</span> ¡Ya está tu viaje!</div>
    <div class="rm-book-hotel">{{ msg.bookingSummary.hotelName }}</div>
    <!-- Un viaje armado trae un localizador por concepto: el voucher del hotel
         y el de cada tour son documentos distintos y el cliente los necesita
         todos. Con una sola reserva se ve igual que siempre. -->
    <template v-if="msg.bookingSummary.items?.length">
      <div v-for="it in msg.bookingSummary.items" :key="it.locator" class="rm-book-row">
        <span>{{ it.nombre }}</span><b>{{ it.locator }}</b>
      </div>
    </template>
    <div v-else class="rm-book-row"><span>Localizador</span><b>{{ msg.bookingSummary.locator }}</b></div>
    <div class="rm-book-row"><span>Entrada</span><b>{{ msg.bookingSummary.checkIn }}</b></div>
    <div class="rm-book-row"><span>Salida</span><b>{{ msg.bookingSummary.checkOut }}</b></div>
    <div class="rm-book-row total"><span>Total pagado</span><b>{{ money(msg.bookingSummary.total, msg.bookingSummary.currency) }}</b></div>
  </div>

  <!-- Fallback texto -->
  <div v-else class="bubble">{{ msg.text }}</div>
</template>
